import {useCallback, useEffect, useMemo} from 'react';
import isEqual from 'react-fast-compare';
import {useDispatch, useSelector} from 'react-redux';

import filterMaskedRows from 'data-frames/util/legacy/filterMaskedRows';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {updateBlockRegressionState} from 'insights/analytics-queries/BlockViewStateReduxActions';
import {BlockViewStateSelector} from 'insights/analytics-queries/reduceBlockViewStates';
import {AnalyticsVisualization} from 'insights/analytics-visualizations/__generated/AnalyticsVisualization';
import performRegressionAnalysis from 'insights/analytics-visualizations/viewers/prop-converters/performRegressionAnalysis';
import {BlockViewContext} from 'insights/universal-blocks/BlockViewContext';
import {BenchlingState} from 'redux-stores/reduceApp';
import {BenchlingDispatch} from 'redux-stores/Store';
import {
  RegressionAnalysis,
  RegressionConfig,
  RegressionDataset,
  RegressionState,
} from 'ui-library/charting/statistics/regression/RegressionTypes';
import pick from 'util/pick';

type OutlierConfig = NonNullable<
  ExtractByTypename<
    AnalyticsVisualization['parameters'],
    'AnalyticsVisualizationScatterPlotParametersNode'
  >['outlierConfig']
>;

export interface UseRegressionBlockViewStateInput {
  inputDataset: RegressionDataset;
  outlierConfig: OutlierConfig | null;
  regressionConfig: RegressionConfig | null;
}

interface UseRegressionBlockViewStateOutput {
  regressionAnalysis: RegressionAnalysis | null;
}

/**
 * Manage the regressionState of a blockViewState in ui.insights.blockViewStates.
 *
 * This hook runs regression calculations based on the input dataset and regression type. The regression is
 * recomputing whenever either the input dataset or regression type changes. The analysis results is returned
 * by the hook and persisted in redux to avoid recomputing the regression between re-mounts.
 *
 * If the inputDataset contains an isMasked column, the regression will be run without the masked points.
 */
export default function useRegressionBlockViewState({
  inputDataset: rawInputDataset,
  outlierConfig,
  regressionConfig,
}: UseRegressionBlockViewStateInput): UseRegressionBlockViewStateOutput {
  const {viewStateId: blockViewStateId} = BlockViewContext.useContext();

  const inputDataset = useMemo(() => filterMaskedRows(rawInputDataset), [rawInputDataset]);

  const regressionState = useSelector((state: BenchlingState) => {
    const blockViewStateSelector = new BlockViewStateSelector(state);
    return blockViewStateSelector.getRegressionState(blockViewStateId);
  });
  const dispatch = useDispatch<BenchlingDispatch>();

  const setRegressionState = useCallback(
    (regressionState: RegressionState | null) => {
      dispatch(updateBlockRegressionState(blockViewStateId, regressionState));
    },
    [blockViewStateId, dispatch]
  );

  useEffect(() => {
    const previousRegressionStateInput =
      regressionState == null
        ? null
        : pick(regressionState, 'inputDataset', 'outlierConfig', 'regressionConfig');

    const regressionInputHasChanged = !isEqual(previousRegressionStateInput, {
      inputDataset,
      outlierConfig,
      regressionConfig,
    });
    if (!regressionInputHasChanged) {
      return;
    }

    if (regressionConfig != null) {
      performRegressionAnalysis(
        inputDataset,
        regressionConfig,
        (regressionAnalysis: RegressionAnalysis) => {
          setRegressionState({
            inputDataset,
            outlierConfig,
            regressionConfig,
            regressionAnalysis,
          });
        },
        outlierConfig
      );
    } else {
      // In case the regression state wasn't properly cleaned up when disabling regression, this hook will
      // clean it up to make sure we don't accidentally render regressions.
      setRegressionState(null);
    }
  }, [inputDataset, outlierConfig, regressionConfig, regressionState, setRegressionState]);

  return {regressionAnalysis: regressionState?.regressionAnalysis ?? null};
}
